import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import OpalPresentationTable from '../DenaliAnalysis/tables/presentation'
import { useHideIfNoDataWhen } from '../HideIfNoData'
import BlueprintTableToolbar from './BlueprintTableToolbar'
import useBlueprintData from './hooks/useBlueprintData'
import { LATCH_LATEST } from './constants'
import useBlueprintColumnDefs, { mapDefaultSort } from './hooks/useBlueprintColumnDefs'
import useColumnConfig from './hooks/useColumnConfig'
import GridColumnConfigModal from './GridColumnConfigModal'
import BlueprintExportButton from './BlueprintExportButton'
import useStyleVariant from './hooks/useStyleVariant'

/**
 * Blueprint-driven presentation table. Receives its full configuration
 * from the blueprint definition — no manual column or sort config needed.
 */
function BlueprintPresentationTable ({ blueprintCode, config, latch, parameters, scopePredicates }) {
  const {
    purposeCode,
    footerPurposeCode,
    footerColumnMapping,
    defaultSort: rawDefaultSort
  } = config

  const effectiveParameters = useMemo(() => {
    if (!latch) return parameters
    return { ...parameters, [latch.parameterName]: LATCH_LATEST }
  }, [parameters, latch])

  const hasFooterPurpose = !!footerPurposeCode && footerPurposeCode !== purposeCode
  const pageSize = config.topN || 1000

  const hasColumnConfig = !!config.enableColumnConfig
  const hasSearch = !!config.enableSearch
  const { mergedColumns, handleColumnConfigSave } = useColumnConfig(
    config.columns, config.localStorageKey
  )
  const [configOpen, setConfigOpen] = useState(false)
  const [globalFilter, setGlobalFilter] = useState('')

  const { data: mainData, isLoading: mainLoading } = useBlueprintData(
    blueprintCode,
    purposeCode,
    { parameters: effectiveParameters, pageSize, scopePredicates }
  )

  const { data: footerData, isLoading: footerLoading } = useBlueprintData(
    blueprintCode,
    hasFooterPurpose ? footerPurposeCode : null,
    { parameters: effectiveParameters, pageSize, scopePredicates }
  )

  const effectiveColumns = hasColumnConfig ? mergedColumns : config.columns

  const { columns, includeFooter } = useBlueprintColumnDefs(
    effectiveColumns,
    footerData,
    footerColumnMapping
  )

  const defaultSort = useMemo(() => mapDefaultSort(rawDefaultSort), [rawDefaultSort])

  const variant = useStyleVariant('Presentation Table', config)

  const visibleColumns = useMemo(() => {
    return columns.filter(col => col.defaultShown ?? true)
  }, [columns])

  const isLoading = mainLoading || (hasFooterPurpose && footerLoading)
  useHideIfNoDataWhen(() => isLoading || !mainData?.length, [isLoading, mainData])

  const showToolbar = !!config.export?.enabled || hasColumnConfig || hasSearch

  const table = (
    <OpalPresentationTable
      {...variant.props}
      className={variant.tableClassName}
      style={variant.styles}
      data={mainData || []}
      columns={visibleColumns}
      defaultSort={defaultSort}
      includeFooter={includeFooter}
      isLoading={isLoading}
      emptyStateText='No Data Available'
      enableFiltering={hasSearch}
      globalFilter={hasSearch ? globalFilter : undefined}
      onGlobalFilterChange={hasSearch ? setGlobalFilter : undefined}
    />
  )

  if (!showToolbar) {
    return variant.className
      ? <div className={variant.className}>{table}</div>
      : table
  }

  return (
    <BlueprintTableToolbar
      className={variant.className}
      hasSearch={hasSearch}
      globalFilter={globalFilter}
      onGlobalFilterChange={setGlobalFilter}
      showExport={!!config.export?.enabled}
      exportButton={
        <BlueprintExportButton
          blueprintCode={blueprintCode}
          config={config}
          purposeCode={purposeCode}
          parameters={effectiveParameters}
          scopePredicates={scopePredicates}
        />
      }
      hasColumnConfig={hasColumnConfig}
      onConfigClick={() => setConfigOpen(true)}
      configModal={
        hasColumnConfig
          ? (
            <GridColumnConfigModal
              open={configOpen}
              onClose={() => setConfigOpen(false)}
              columns={mergedColumns}
              defaultColumns={config.columns}
              onSave={handleColumnConfigSave}
            />
          )
          : null
      }
    >
      {table}
    </BlueprintTableToolbar>
  )
}

BlueprintPresentationTable.propTypes = {
  /** The blueprint code (used for data fetching) */
  blueprintCode: PropTypes.string.isRequired,

  /** The resolved presentation config from the blueprint definition */
  config: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      hidden: PropTypes.bool,
      exclude: PropTypes.bool,
      sortable: PropTypes.bool,
      filterable: PropTypes.bool,
      formatString: PropTypes.string
    })),
    defaultSort: PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.string,
      dir: PropTypes.string
    })),
    purposeCode: PropTypes.string.isRequired,
    localStorageKey: PropTypes.string,
    footerPurposeCode: PropTypes.string,
    footerColumnMapping: PropTypes.arrayOf(PropTypes.shape({
      mainColumn: PropTypes.string,
      footerColumn: PropTypes.string
    })),
    variationGroups: PropTypes.arrayOf(PropTypes.string),
    topN: PropTypes.number,
    enableColumnConfig: PropTypes.bool,
    enableSearch: PropTypes.bool,
    export: PropTypes.shape({
      enabled: PropTypes.bool,
      formats: PropTypes.arrayOf(PropTypes.string),
      defaultFileName: PropTypes.string
    })
  }).isRequired,

  /** Latch configuration from the purpose (passed by QBPPresentation) */
  latch: PropTypes.object,

  /** Runtime parameters for variable substitution */
  parameters: PropTypes.object,

  /** Scope predicates derived from blueprint scope resolution and context */
  scopePredicates: PropTypes.arrayOf(PropTypes.object)
}

BlueprintPresentationTable.defaultProps = {
  latch: null,
  parameters: {}
}

export default BlueprintPresentationTable
